import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './MarketData.css';

// Chart using Recharts
const PriceTrendChart = ({ transactions }) => {
  const chartData = useMemo(() => {
    if (!transactions || transactions.length < 2) return [];

    // 1. Filter and Sort
    const sortedData = [...transactions]
      .filter(t => {
        if (!t.pricePerMeter || !t.date) return false;
        return t.pricePerMeter >= 1000 && t.pricePerMeter <= 200000;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (sortedData.length < 2) return [];

    // 2. Apply Smoothing (Moving Average) to create a Trend Line
    // Dynamic window size: larger window for more data points to reduce noise
    const windowSize = Math.max(5, Math.floor(sortedData.length / 10));

    return sortedData.map((t, index) => {
      // Calculate centered moving average
      const start = Math.max(0, index - Math.floor(windowSize / 2));
      const end = Math.min(sortedData.length, index + Math.floor(windowSize / 2) + 1);
      const subset = sortedData.slice(start, end);
      const avgPrice = subset.reduce((sum, item) => sum + item.pricePerMeter, 0) / subset.length;

      const dateObj = new Date(t.date);
      // Format as "Sep 25" (short month + 2-digit year)
      const shortMonth = dateObj.toLocaleDateString('en-US', { month: 'short' });
      const shortYear = dateObj.getFullYear().toString().slice(-2);
      
      return {
        date: t.date,
        displayDate: `${shortMonth} ${shortYear}`,
        timestamp: dateObj.getTime(),
        price: Math.round(avgPrice), // Smoothed price
        fullDate: dateObj.toLocaleDateString('he-IL')
      };
    });
  }, [transactions]);

  if (chartData.length < 2) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'var(--bg-surface)',
          padding: '8px 12px',
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-sm)',
          direction: 'rtl'
        }}>
          <p className="label" style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>{data.fullDate}</p>
          <p className="price" style={{ margin: '4px 0 0 0', color: 'var(--text-primary)', fontWeight: 700 }}>
            ₪{data.price.toLocaleString()} למ"ר
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <h4 className="section-subtitle">מגמת מחירים (₪/מ"ר)</h4>
      <div className="chart-wrapper" style={{ height: 300, paddingRight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-data-emerald)" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="var(--color-data-emerald)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-light)" />
            <XAxis 
              dataKey="displayDate" 
              tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
              minTickGap={30}
            />
            <YAxis 
              tickFormatter={(value) => `₪${(value/1000).toFixed(0)}k`}
              tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              domain={['auto', 'auto']}
              orientation="left" // Force Y axis to left side (LTR standard for numbers even in RTL context usually, or better for layout)
              // In RTL layout, YAxis on right might be preferred, but Recharts can be tricky. 
              // Let's try putting it on the right by mirroring? No, let's stick to left or right prop.
              // orientation="right" matches the previous SVG layout?
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="price"
              stroke="var(--color-data-emerald)"
              fillOpacity={1}
              fill="url(#colorPrice)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};


const MarketData = ({ marketData, pricePerSqm }) => {
  const [filterRooms, setFilterRooms] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'asc' });

  const transactions = marketData?.transactions;

  // 1. First Pass: Filter Outliers & Calculate Score (Memoized)
  const baseTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    // Calculate price per meter stats for outlier detection
    const prices = transactions.map(t => t.pricePerMeter).filter(p => p > 0);
    const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - avgPrice, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    
    const upperLimit = avgPrice + (2 * stdDev);
    const lowerLimit = Math.max(0, avgPrice - (2 * stdDev));

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    
    return transactions
      .filter(t => {
        if (!t.date) return false;
        // Filter out price outliers
        if (t.pricePerMeter > upperLimit || t.pricePerMeter < lowerLimit) return false;
        const date = new Date(t.date);
        return date >= twoYearsAgo && !isNaN(date.getTime());
      })
      .map(t => {
        // Calculate Score
        const date = new Date(t.date);
        const daysOld = (new Date() - date) / (1000 * 60 * 60 * 24);
        const distance = t.distance !== undefined ? t.distance : 1000;
        // Lower score is better
        const score = (distance * 1.5) + (daysOld * 1.0);
        return { ...t, score };
      });
  }, [transactions]);

  // 2. Second Pass: Filter by Rooms & Select Top 100 "Smart Matches"
  const topTransactions = useMemo(() => {
    let result = [...baseTransactions];

    // A. Filter by Rooms first (so we get the best 100 of the selected type)
    if (filterRooms !== 'all') {
      const targetRooms = parseFloat(filterRooms);
      result = result.filter(t => {
        const rooms = t.beds || t.rooms;
        return rooms === targetRooms; 
      });
    }

    // B. Always sort by "Smart Score" first to define the "Best 100"
    // This ensures the list content remains stable regardless of UI sorting
    result.sort((a, b) => (a.score || Infinity) - (b.score || Infinity));

    // C. Take the top 100
    return result.slice(0, 100);
  }, [baseTransactions, filterRooms]);

  // 3. Third Pass: Apply User UI Sorting to the Top 100
  const processedTransactions = useMemo(() => {
    let result = [...topTransactions];

    // Sort the existing 100 items by the user's chosen column
    if (sortConfig.key !== 'score') {
      result.sort((a, b) => {
        let valA, valB;

        switch (sortConfig.key) {
          case 'address':
            valA = a.address || '';
            valB = b.address || '';
            return sortConfig.direction === 'asc' 
              ? valA.localeCompare(valB) 
              : valB.localeCompare(valA);
          case 'distance':
            valA = (a.distance !== undefined && a.distance !== null) ? a.distance : Infinity;
            valB = (b.distance !== undefined && b.distance !== null) ? b.distance : Infinity;
            break;
          case 'date':
            valA = new Date(a.date).getTime();
            valB = new Date(b.date).getTime();
            break;
          case 'amount':
            valA = a.amount || 0;
            valB = b.amount || 0;
            break;
          case 'size':
            valA = a.size || 0;
            valB = b.size || 0;
            break;
          case 'beds':
            valA = a.beds || 0;
            valB = b.beds || 0;
            break;
          case 'pricePerMeter':
            valA = a.pricePerMeter || 0;
            valB = b.pricePerMeter || 0;
            break;
          default:
            return 0;
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [topTransactions, sortConfig]);

  // Calculate derived stats from visible filtered transactions
  const derivedStats = useMemo(() => {
    if (processedTransactions.length === 0) return null;

    const prices = processedTransactions.map(t => t.pricePerMeter);
    const minPrice = Math.round(Math.min(...prices));
    const maxPrice = Math.round(Math.max(...prices));
    const avgPrice = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    const sorted = [...prices].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const medianPrice = Math.round(
      sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
    );

    return {
      priceRange: { min: minPrice, max: maxPrice },
      averagePrice: avgPrice,
      medianPrice,
    };
  }, [processedTransactions]);

  // Extra insights derived from the comps we already have: 2-year momentum,
  // comp quality (recency / proximity / year-built mix), and a typical-unit
  // value reckoner. All from baseTransactions — no extra data fetched.
  const marketExtras = useMemo(() => {
    if (!baseTransactions || baseTransactions.length < 4) return null;
    const now = new Date();
    const oneYearAgo = new Date(); oneYearAgo.setFullYear(now.getFullYear() - 1);

    // Momentum: avg ₪/m² of the most recent year vs the prior year
    const recent = [], prior = [];
    baseTransactions.forEach(t => {
      const d = new Date(t.date);
      (d >= oneYearAgo ? recent : prior).push(t.pricePerMeter);
    });
    const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    const recentAvg = avg(recent), priorAvg = avg(prior);
    const momentumPct = (recentAvg && priorAvg)
      ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100)
      : null;

    // Proximity bands (non-cumulative, so they read like the year-built bands)
    const dist = baseTransactions.map(t => t.distance).filter(d => Number.isFinite(d));
    const distBands = dist.length ? {
      near: dist.filter(d => d <= 100).length,
      mid: dist.filter(d => d > 100 && d <= 250).length,
      far: dist.filter(d => d > 250 && d <= 500).length,
      total: dist.length,
    } : null;

    // Year-built mix
    const built = baseTransactions.map(t => t.yearBuilt).filter(y => Number.isFinite(y) && y > 1900);
    const yearMix = built.length ? {
      old: built.filter(y => y < 1980).length,
      mid: built.filter(y => y >= 1980 && y < 2005).length,
      new: built.filter(y => y >= 2005).length,
      total: built.length,
    } : null;

    // Typical-unit value reckoner: median comp size × average ₪/m²
    const sizes = baseTransactions.map(t => t.size).filter(s => Number.isFinite(s) && s > 10).sort((a, b) => a - b);
    const typicalSize = sizes.length ? Math.round(sizes[Math.floor(sizes.length / 2)]) : null;

    return { momentumPct, distBands, yearMix, typicalSize };
  }, [baseTransactions]);

  // Helper for sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (name) => {
    if (sortConfig.key !== name) return <i className="fi fi-rr-sort-alt opacity-30"></i>;
    return sortConfig.direction === 'asc' 
      ? <i className="fi fi-rr-sort-amount-down-alt"></i> 
      : <i className="fi fi-rr-sort-amount-up-alt"></i>;
  };

  if (!marketData && !pricePerSqm) return null;

  // Check if we have real market data (transactions, pricePerMeter from API, or insights)
  const hasRealTransactions = transactions && transactions.length > 0;
  const hasRealPriceData = marketData?.pricePerMeter && marketData.pricePerMeter > 0 && marketData?.dataSource !== 'fallback_estimate';
  const hasMarketInsights = marketData?.marketInsights && marketData.marketInsights.length > 0;
  const hasRealData = hasRealTransactions || hasRealPriceData || hasMarketInsights;
  
  // If we have no real data at all - show graceful "no data" message
  if (!hasRealData) {
    return (
      <div className="market-data-card no-data-state">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-chart-histogram"></i> נתוני שוק ומחירים</h3>
        </div>
        <div className="market-data-content">
          <div className="no-market-data-container">
            <div className="no-data-icon">
              <i className="fi fi-rr-chart-line-up"></i>
            </div>
            <h4 className="no-data-title">לא נמצאו נתוני שוק עבור כתובת זו</h4>
            <p className="no-data-description">
              לא הצלחנו לאתר עסקאות נדל"ן או נתוני מחירים עדכניים עבור הנכס הזה.
              <br />
              ייתכן שהכתובת חדשה או שאין מספיק עסקאות מתועדות באזור.
            </p>
            <div className="no-data-suggestions">
              <span className="suggestion-label">מה ניתן לעשות:</span>
              <ul>
                <li>לבדוק נכסים סמוכים באותו הרחוב</li>
                <li>לפנות לשמאי מקרקעין לקבלת הערכה מקצועית</li>
                <li>לבדוק באתרי נדל"ן נוספים (מדלן, יד2)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Destructure data for main view
  const { pricePerMeter, priceRange, dataSource } = marketData || {};
  const isRealData = dataSource === 'market_source' || dataSource === 'market_data';
  
  const displayPriceRange = derivedStats?.priceRange || priceRange;
  const displayAvgPrice = derivedStats?.averagePrice || pricePerMeter || pricePerSqm;
  const hasDisplayAvgPrice = Number.isFinite(displayAvgPrice) && displayAvgPrice > 0 && dataSource !== 'fallback_estimate';
  const hasDisplayRange = displayPriceRange 
    && Number.isFinite(displayPriceRange.min) 
    && Number.isFinite(displayPriceRange.max) 
    && displayPriceRange.min > 0 
    && displayPriceRange.max > 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // Extract unique room counts for filter
  const availableRoomCounts = [...new Set(baseTransactions.map(t => t.beds || t.rooms))].sort((a,b) => a-b);

  // ----- "Comps Dashboard A" view-model, derived from the real comps -----
  const clampPct = (v) => Math.max(0, Math.min(100, v));
  const subjectSize = marketExtras?.typicalSize || null;
  const estValue = (subjectSize && displayAvgPrice) ? Math.round(displayAvgPrice * subjectSize) : null;
  const rMin = displayPriceRange?.min;
  const rMax = displayPriceRange?.max;
  const rangeSpan = (Number.isFinite(rMin) && Number.isFinite(rMax) && rMax > rMin) ? (rMax - rMin) : null;
  const pricePos = (rangeSpan && displayAvgPrice) ? clampPct(((displayAvgPrice - rMin) / rangeSpan) * 100) : null;

  const distItems = marketExtras?.distBands ? [
    { label: "עד 100 מ'", n: marketExtras.distBands.near },
    { label: "100–250 מ'", n: marketExtras.distBands.mid },
    { label: "250–500 מ'", n: marketExtras.distBands.far },
  ] : [];
  const yearItems = marketExtras?.yearMix ? [
    { label: '2005 ואילך', n: marketExtras.yearMix.new },
    { label: '1980–2005', n: marketExtras.yearMix.mid },
    { label: 'לפני 1980', n: marketExtras.yearMix.old },
  ] : [];
  const distMax = Math.max(1, ...distItems.map((d) => d.n));
  const yearMax = Math.max(1, ...yearItems.map((d) => d.n));
  const relevanceTotal = distItems.reduce((s, d) => s + d.n, 0);

  // Per-room aggregation for the rooms panel
  const roomAgg = {};
  baseTransactions.forEach((tx) => {
    const rooms = tx.beds || tx.rooms;
    if (!rooms) return;
    const amount = tx.amount || (tx.pricePerMeter && tx.size ? tx.pricePerMeter * tx.size : 0);
    if (!amount || amount < 100000) return;
    (roomAgg[rooms] = roomAgg[rooms] || []).push(amount);
  });
  const roomBars = Object.entries(roomAgg).map(([rooms, amts]) => ({
    rooms: parseFloat(rooms),
    avg: amts.reduce((a, b) => a + b, 0) / amts.length,
    min: Math.min(...amts),
    max: Math.max(...amts),
    deals: amts.length,
  })).sort((a, b) => a.rooms - b.rooms);
  const roomScaleMax = Math.max(1, ...roomBars.map((r) => r.max));
  const fmtM = (n) => '₪' + (n / 1000000).toFixed(2) + 'M';
  const distDotColor = (d) => (d <= 50 ? '#2985ad' : d <= 120 ? '#9cc2d4' : '#cbd2da');

  return (
    <div className={`market-data-card md-dashboard ${isRealData ? 'real-data' : 'fallback-data'}`}>
      <div className="market-data-header">
        <h3 className="market-data-title"><i className="fi fi-rr-chart-histogram"></i> נתוני שוק ומחירים</h3>
        {isRealData && (
          <span className="data-badge real"><i className="fi fi-rr-check"></i> נתוני אמת</span>
        )}
      </div>

      <div className="market-data-content">
        {/* HERO: estimated value + the average's position within the area range */}
        {hasDisplayAvgPrice && (
          <div className="md-hero">
            <div className="md-hero-value">
              <span className="md-hero-label">{estValue ? 'שווי מוערך לדירה טיפוסית באזור' : 'מחיר ממוצע למ"ר'}</span>
              <span className="md-hero-amount" dir="ltr">₪{(estValue || displayAvgPrice).toLocaleString()}</span>
              <span className="md-hero-sub">
                {subjectSize ? <>~{subjectSize} מ"ר · </> : null}<span dir="ltr">₪{displayAvgPrice.toLocaleString()}</span> למ"ר
              </span>
            </div>
            {pricePos != null && (
              <>
                <div className="md-hero-divider" />
                <div className="md-hero-range">
                  <span className="md-hero-label">מיקום המחיר הממוצע בטווח האזור (₪ למ"ר)</span>
                  <div className="md-range-track">
                    <span className="md-range-dot" style={{ left: `${pricePos}%` }} />
                    <span className="md-range-dot-label" style={{ left: `${pricePos}%` }} dir="ltr">₪{displayAvgPrice.toLocaleString()}</span>
                  </div>
                  <div className="md-range-scale">
                    <span dir="ltr">₪{rMin.toLocaleString()}</span>
                    {derivedStats?.medianPrice > 0 && <span className="md-range-mid">חציון ₪{derivedStats.medianPrice.toLocaleString()}</span>}
                    <span dir="ltr">₪{rMax.toLocaleString()}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* KPI trio: range / median / average (+ momentum) */}
        {hasDisplayAvgPrice && (
          <div className="md-kpi-trio">
            {hasDisplayRange && (
              <div className="md-kpi">
                <span className="md-kpi-label">טווח מחירים למ"ר באזור</span>
                <span className="md-kpi-value" dir="ltr">₪{rMin.toLocaleString()} – ₪{rMax.toLocaleString()}</span>
                <span className="md-kpi-sub">מהזול ליקר בעסקאות</span>
              </div>
            )}
            {derivedStats?.medianPrice > 0 && (
              <div className="md-kpi">
                <span className="md-kpi-label">מחיר חציוני למ"ר</span>
                <span className="md-kpi-value" dir="ltr">₪{derivedStats.medianPrice.toLocaleString()}</span>
                <span className="md-kpi-sub">חצי מהעסקאות מעל ערך זה</span>
              </div>
            )}
            <div className="md-kpi">
              <span className="md-kpi-label">מחיר ממוצע למ"ר</span>
              <span className="md-kpi-value-row">
                <span className="md-kpi-value accent" dir="ltr">₪{displayAvgPrice.toLocaleString()}</span>
                {marketExtras?.momentumPct != null && (
                  <span className={`md-momentum ${marketExtras.momentumPct >= 0 ? 'up' : 'down'}`}>
                    {marketExtras.momentumPct >= 0 ? '▴ +' : '▾ '}{marketExtras.momentumPct}%
                  </span>
                )}
              </span>
              <span className="md-kpi-sub">בשנה האחרונה</span>
            </div>
          </div>
        )}

        {/* Relevance: how comparable are the deals (distance / construction era) */}
        {relevanceTotal > 0 && (
          <div className="md-relevance">
            <div className="md-relevance-head">רלוונטיות ההשוואה<span>{relevanceTotal} עסקאות</span></div>
            <div className="md-relevance-cols">
              <div className="md-relevance-col">
                <span className="md-relevance-sub">לפי מרחק מהנכס</span>
                {distItems.map((d, i) => (
                  <div className="md-bar-row" key={i}>
                    <span className="md-bar-label">{d.label}</span>
                    <span className="md-bar-track"><span className="md-bar-fill blue" style={{ width: `${(d.n / distMax) * 100}%` }} /></span>
                    <span className="md-bar-n">{d.n}</span>
                  </div>
                ))}
              </div>
              {yearItems.length > 0 && (
                <div className="md-relevance-col">
                  <span className="md-relevance-sub">לפי שנת בנייה</span>
                  {yearItems.map((d, i) => (
                    <div className="md-bar-row" key={i}>
                      <span className="md-bar-label">{d.label}</span>
                      <span className="md-bar-track"><span className="md-bar-fill teal" style={{ width: `${(d.n / yearMax) * 100}%` }} /></span>
                      <span className="md-bar-n">{d.n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Market insights — short, punchy takeaways only (the source can return
            long-form paragraphs, so prefer concise/verified ones and cap length) */}
        {(() => {
          const insights = (marketData?.marketInsights || [])
            .map((ins) => ({ ...ins, text: String(ins?.text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() }))
            .filter((ins) => ins.text.length > 0 && !/[<>{}]/.test(ins.text))
            .sort((a, b) => (b.verified === a.verified ? a.text.length - b.text.length : (b.verified ? 1 : -1)))
            .filter((ins) => ins.text.length <= 160)
            .slice(0, 4);
          if (!insights.length) return null;
          return (
            <div className="market-insights">
              <span className="market-insights-title"><i className="fi fi-rr-bulb"></i> תובנות שוק</span>
              <ul className="market-insights-list">
                {insights.map((ins, i) => <li key={i}>{ins.text}</li>)}
              </ul>
            </div>
          );
        })()}

        {/* Two columns: price trend + price-by-rooms */}
        <div className="md-two-col">
          <div className="md-panel">
            {transactions && transactions.length > 1 ? (
              <PriceTrendChart transactions={transactions} />
            ) : (
              <div className="no-data-message">
                <i className="fi fi-rr-chart-histogram"></i>
                <span>לא נמצאו מספיק נתונים היסטוריים להצגת גרף מגמה עבור נכס זה</span>
              </div>
            )}
          </div>

          <div className="md-panel">
            <div className="md-panel-head">
              <span className="md-panel-title">מחיר דירה ממוצע לפי חדרים</span>
              <span className="md-panel-hint">אורך הסרגל = מחיר ממוצע</span>
            </div>
            {roomBars.length > 0 ? roomBars.map((r) => {
              const conf = r.deals >= 15 ? 'high' : r.deals >= 5 ? 'med' : 'low';
              const avgPct = clampPct((r.avg / roomScaleMax) * 100);
              return (
                <div className={`md-room-row ${conf === 'low' ? 'faded' : ''}`} key={r.rooms}>
                  <span className="md-room-label">{r.rooms}<small> חד'</small></span>
                  <span className="md-room-track">
                    <span className="md-room-avg" style={{ width: `${avgPct}%` }} />
                  </span>
                  <span className="md-room-avgval" dir="ltr">{fmtM(r.avg)}</span>
                  <span className="md-room-deals">{r.deals} עסקאות</span>
                </div>
              );
            }) : (
              <div className="no-data-message"><span>אין מספיק נתונים לפי חדרים</span></div>
            )}
          </div>
        </div>

        {/* Recent comparable transactions */}
        <div className="md-panel md-comps">
          <div className="md-panel-head">
            <span className="md-panel-title">עסקאות אחרונות <small>(שנתיים אחרונות)</small></span>
            <div className="room-filter">
              <span className="filter-label">חדרים:</span>
              <select
                id="market-rooms-filter"
                name="rooms_filter"
                autoComplete="off"
                value={filterRooms}
                onChange={(e) => setFilterRooms(e.target.value)}
                className="room-filter-select"
              >
                <option value="all">הכל</option>
                {availableRoomCounts.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          {processedTransactions.length > 0 ? (
            <div className="transactions-table-wrapper">
              <table className="transactions-table md-comps-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort('address')} className="sortable">כתובת {getSortIcon('address')}</th>
                    <th onClick={() => requestSort('distance')} className="sortable">מרחק {getSortIcon('distance')}</th>
                    <th onClick={() => requestSort('date')} className="sortable">תאריך {getSortIcon('date')}</th>
                    <th onClick={() => requestSort('amount')} className="sortable">מחיר {getSortIcon('amount')}</th>
                    <th onClick={() => requestSort('size')} className="sortable">שטח {getSortIcon('size')}</th>
                    <th onClick={() => requestSort('beds')} className="sortable">חדרים {getSortIcon('beds')}</th>
                    <th onClick={() => requestSort('pricePerMeter')} className="sortable">מחיר למ"ר {getSortIcon('pricePerMeter')}</th>
                  </tr>
                </thead>
                <tbody>
                  {processedTransactions.map((tx, index) => {
                    const dev = displayAvgPrice ? (tx.pricePerMeter - displayAvgPrice) / displayAvgPrice : 0;
                    const tone = dev < -0.03 ? 'low' : dev > 0.03 ? 'high' : 'mid';
                    return (
                      <tr key={index}>
                        <td className="address-cell">{tx.address || '-'}</td>
                        <td>
                          <span className="md-dist">
                            <span className="md-dist-dot" style={{ background: distDotColor(tx.distance ?? 999) }} />
                            {tx.distance !== undefined ? `${Math.round(tx.distance)} מ'` : '-'}
                          </span>
                        </td>
                        <td dir="ltr">{formatDate(tx.date)}</td>
                        <td dir="ltr">₪{Math.round(tx.amount).toLocaleString()}</td>
                        <td dir="ltr">{tx.size} מ"ר</td>
                        <td style={{ textAlign: 'center' }}>{tx.beds}</td>
                        <td>
                          <span className={`md-ppm ${tone}`} dir="ltr">
                            {tone === 'low' ? '▾' : tone === 'high' ? '▴' : '·'} ₪{Math.round(tx.pricePerMeter).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <i className="fi fi-rr-time-past"></i>
              <span>לא נמצאו עסקאות תואמות לסינון זה</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketData;
